import React, {
  useReducer,
  useContext,
  useState,
  useEffect,
  Dispatch,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";

// Define the initial user state
export const initialState = {
  isAuthenticated: !!localStorage.getItem("id_token"),
  user: JSON.parse(localStorage.getItem("currentUser") || "null"),
};

// User reducer function
export function UserReducer(state: any, action: any) {
  switch (action.type) {
    case "LOGIN_SUCCESS":
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload,
      };
    case "LOGIN_FAILURE":
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    case "LOGOUT":
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    default:
      return state;
  }
}

// Define types for our contexts
type UserStateType = any; // Replace with your actual user state type
type UserDispatchType = Dispatch<any>; // Replace with your actual action type

// Create Auth Context for login state
interface AuthContextType {
  isLoggedIn: boolean;
  setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
  logOutUser: () => void;
}

const UserStateContext = React.createContext<UserStateType | undefined>(
  undefined
);
const UserDispatchContext = React.createContext<UserDispatchType | undefined>(
  undefined
);
const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

// Create a new context for system admin authorization
const SystemAdminContext = React.createContext({
  isSystemAdmin: false,
  loading: true,
});

export function useUserState() {
  const context = React.useContext(UserStateContext);
  if (context === undefined) {
    throw new Error("useUserState must be used within a Provider");
  }
  return context;
}

export function useUserDispatch() {
  const context = React.useContext(UserDispatchContext);
  if (context === undefined) {
    throw new Error("useUserDispatch must be used within a Provider");
  }
  return context;
}

/**
 * Hook to access global authentication state and functions
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a UserProvider");
  }
  return context;
}

/**
 * Hook to check if the current user is a system admin
 * Returns isSystemAdmin, loading status, and isUnauthorized flag
 */
export function useSystemAdminCheck() {
  const context = useContext(SystemAdminContext);
  if (context === undefined) {
    throw new Error("useSystemAdminCheck must be used within a UserProvider");
  }

  // If not a system admin and not loading, set isUnauthorized to true
  if (!context.loading && !context.isSystemAdmin) {
    return {
      ...context,
      isUnauthorized: true,
    };
  }

  return {
    ...context,
    isUnauthorized: false,
  };
}

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, dispatch] = useReducer(UserReducer, initialState);
  const [systemAdminState, setSystemAdminState] = useState({
    isSystemAdmin: false,
    loading: true,
  });
  const [isLoggedIn, setIsLoggedIn] = useState(
    localStorage.getItem("userAuthorized") === "true"
  );
  const navigate = useNavigate();

  // Check if user is system admin on mount
  useEffect(() => {
    try {
      const userJson = localStorage.getItem("currentUser");

      if (userJson) {
        const currentUser = JSON.parse(userJson);
        setSystemAdminState({
          isSystemAdmin: currentUser.isSystemAdmin === true,
          loading: false,
        });
      } else {
        setSystemAdminState({
          isSystemAdmin: false,
          loading: false,
        });
      }
    } catch (error) {
      console.error("Error checking system admin status:", error);
      setSystemAdminState({
        isSystemAdmin: false,
        loading: false,
      });
    }
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const checkLoginStatus = () => {
      const loginStatus = localStorage.getItem("userAuthorized") === "true";
      setIsLoggedIn(loginStatus);
    };

    // Check on mount
    checkLoginStatus();

    // Set up event listener for storage changes
    window.addEventListener("storage", checkLoginStatus);

    return () => {
      window.removeEventListener("storage", checkLoginStatus);
    };
  }, []);

  // Centralized logout function
  const logOutUser = () => {
    // Remove all auth-related items
    localStorage.removeItem("userAuthorized");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("token");
    localStorage.removeItem("id_token");

    // Update local state
    setIsLoggedIn(false);

    // Update context state
    dispatch({ type: "LOGOUT" });

    // Trigger storage event
    window.dispatchEvent(new Event("storage"));

    // Navigate to login
    navigate("/login");
  };

  // Create auth context value
  const authContextValue = {
    isLoggedIn,
    setIsLoggedIn,
    logOutUser,
  };

  return (
    <UserStateContext.Provider value={user}>
      <UserDispatchContext.Provider value={dispatch}>
        <AuthContext.Provider value={authContextValue}>
          <SystemAdminContext.Provider value={systemAdminState}>
            {children}
          </SystemAdminContext.Provider>
        </AuthContext.Provider>
      </UserDispatchContext.Provider>
    </UserStateContext.Provider>
  );
};

// Define auth functions
export async function loginUser(dispatch: any, userInfo: any) {
  dispatch({ type: "LOGIN_SUCCESS", payload: userInfo });
}

export async function logout(dispatch: any) {
  // Remove all authentication-related items from localStorage
  localStorage.removeItem("id_token");
  localStorage.removeItem("currentUser");
  localStorage.removeItem("token");
  localStorage.removeItem("userAuthorized");

  // Dispatch logout action to update context state
  dispatch({ type: "LOGOUT" });

  // Trigger storage event for other components listening to localStorage changes
  window.dispatchEvent(new Event("storage"));
}
